import { Option } from "@inrev/core";
import { type FC, type Ref } from "react";
import Currency from "react-currency-input-field";
import { Tooltip } from "react-tooltip";
import { cn } from "../../lib/utils";
import { inputSize } from "./inputSize";

export type CurrencyPercentInputMode = "dollars" | "percent";

export type CurrencyPercentInputProps = {
	inputMode: CurrencyPercentInputMode;
	setInputMode: (mode: CurrencyPercentInputMode) => void;
	currencyValue: string | number | null | undefined;
	percentValue: string | number | null | undefined;
	onCurrencyChange: (value: string | number) => void;
	onPercentChange: (value: number | undefined) => void;
	baseAmount: number;
	onBlur?: () => void;
	updateOnBlur?: boolean;
	compact?: boolean;
	error?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	className?: string;
	testId?: string;
	ref?: Ref<HTMLInputElement>;
};

export const CurrencyPercentInput: FC<CurrencyPercentInputProps> = ({
	inputMode,
	setInputMode,
	currencyValue,
	percentValue,
	onCurrencyChange,
	onPercentChange,
	baseAmount,
	onBlur,
	updateOnBlur,
	compact,
	error = false,
	disabled = false,
	readonly = false,
	className,
	testId,
	ref,
}) => {
	const { frame, paddingX } = inputSize(compact);

	const handleChange = (value?: string, name?: CurrencyPercentInputMode) => {
		if (baseAmount <= 0) return;
		const numericValue = value?.replace(/[^0-9.-]/g, "") || "0";
		if (name === "dollars") {
			if (numericValue && parseFloat(numericValue) > baseAmount) onCurrencyChange(baseAmount);
			else onCurrencyChange(numericValue);
		} else {
			const parsed = Math.max(0, Math.min(parseFloat(numericValue), 100));
			onPercentChange(parsed / 100);
		}
	};

	const getCurrencyValue = () => {
		if (typeof currencyValue === "number" && !Number.isNaN(currencyValue))
			return currencyValue.toString();
		if (typeof currencyValue === "number" && Number.isNaN(currencyValue)) return "";
		return currencyValue ?? "";
	};

	const getPercentValue = () => {
		if (Option.isNone(percentValue)) return "";
		const numeric =
			typeof percentValue === "number" ? percentValue : parseFloat(String(percentValue)) || 0;
		const value = (Math.round(numeric * 100 * 1000) / 1000).toString();
		return value === "NaN" ? "" : value;
	};

	const getButtonClass = (mode: CurrencyPercentInputMode, extraClassName?: string) => {
		return cn(
			"h-full flex items-center justify-center transition-colors outline-none",
			compact ? "min-w-[34px]" : "min-w-[50px]",
			inputMode === mode
				? "bg-brand-accent text-inverse font-semibold"
				: "bg-surface-neutral text-tertiary hover:text-secondary",
			readonly && inputMode !== mode && "text-tertiary",
			extraClassName,
		);
	};

	const inputClassName = cn(
		"w-full h-full text-center bg-transparent outline-none order-3 rounded-r-md",
		paddingX,
		readonly && "text-primary",
	);

	return (
		<div
			className={cn(
				"relative flex w-full rounded-md outline outline-[1px] outline-offset-[-1px]",
				"text-primary outline-bc-strong bg-surface-light/70",
				"placeholder:text-tertiary/85 focus-within:outline-[2px]",
				"focus-within:outline-text-secondary focus-within:hover:outline-text-secondary",
				frame,
				disabled ? "cursor-not-allowed opacity-50" : "hover:outline-text-tertiary",
				readonly && "bg-surface-neutral/70 outline-[0px]",
				error &&
					"outline-danger focus-within:outline-danger focus-within:hover:outline-danger hover:outline-danger hover:outline-2",
				className,
			)}
			onBlur={onBlur}
		>
			{inputMode === "dollars" ? (
				<Currency
					disabled={readonly}
					placeholder="0.00"
					onFocus={(e) => e.target.select()}
					ref={ref}
					tabIndex={0}
					decimalScale={2}
					className={inputClassName}
					onBlur={updateOnBlur ? onBlur : undefined}
					onValueChange={(value) => handleChange(value, "dollars")}
					data-1p-ignore
					data-lpignore
					data-testid={testId}
					value={getCurrencyValue()}
					allowNegativeValue={false}
				/>
			) : (
				<input
					type="text"
					value={getPercentValue()}
					disabled={readonly || disabled}
					placeholder="0"
					onFocus={(e) => e.target.select()}
					ref={ref}
					tabIndex={0}
					className={inputClassName}
					onBlur={updateOnBlur ? onBlur : undefined}
					onChange={(e) => handleChange(e.target.value, "percent")}
					autoComplete="one-time-code"
					data-1p-ignore
					data-lpignore
					data-testid={testId}
					max={100}
				/>
			)}
			<button
				type="button"
				tabIndex={-1}
				onClick={() => setInputMode("dollars")}
				disabled={readonly || disabled}
				className={getButtonClass("dollars", "order-1 rounded-l-md")}
				data-tooltip-id="bond-amount-as-percent-of-bid"
				data-tooltip-content="Click to change to Bond Amount as Dollars"
			>
				$
			</button>
			{inputMode === "percent" && !disabled && <Tooltip id="bond-amount-as-percent-of-bid" />}
			<button
				type="button"
				tabIndex={-1}
				onClick={() => setInputMode("percent")}
				disabled={readonly || disabled}
				className={getButtonClass("percent", "order-2")}
				data-tooltip-id="bond-amount-as-dollars"
				data-tooltip-content="Click to change to Bond Amount as Percent of Bid"
			>
				%
			</button>
			{inputMode === "dollars" && !disabled && <Tooltip id="bond-amount-as-dollars" />}
		</div>
	);
};
